// ═══════════════════════════════════════════════════════════════
// useInspection — one inspection doc per job, id === jobId.
//
// The doc mirrors the checklist template's section/item structure,
// with the labels copied in alongside the answers. That duplication
// is deliberate: editing a template in Settings must never rewrite
// the history of an inspection that already went to a customer.
// ═══════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { inspectionsCol } from '../database.js';
import { blankInspectionFromChecklist } from './inspection-template.js';
import { inspectionIsComplete, normalizeInspection } from './inspection-helpers.js';

export function useInspection(jobId) {
  const [inspection, setInspection] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!jobId) {
      setInspection(null);
      setReady(true);
      return;
    }
    setReady(false);
    const ref = doc(inspectionsCol(), jobId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setInspection(
          snap.exists()
            ? normalizeInspection({ id: snap.id, ...snap.data() })
            : null
        );
        setReady(true);
      },
      (err) => {
        console.error('Inspection subscription failed:', err);
        setReady(true);
      }
    );
    return unsub;
  }, [jobId]);

  // Create the inspection for this job from the given checklist.
  // Returns the created doc so callers can drive the wizard without
  // waiting for the subscription to round-trip.
  async function startInspection(checklist) {
    if (!jobId) throw new Error('No job id');
    const payload = {
      jobId,
      ...blankInspectionFromChecklist(checklist),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(inspectionsCol(), jobId), payload, { merge: false });
    return { id: jobId, ...payload };
  }

  // Persist a whole inspection. completedAt flips on once every item
  // has an answer, and back off if the tech reopens and clears one.
  async function saveInspection(next) {
    if (!jobId) return;
    const complete = inspectionIsComplete(next);
    await setDoc(
      doc(inspectionsCol(), jobId),
      {
        jobId,
        sections: next.sections,
        extraItems: next.extraItems ?? [],
        // Retaken originals may appear on previously sent customer reports.
        archivedPhotoPaths: next.archivedPhotoPaths ?? [],
        templateId: next.templateId ?? null,
        templateName: next.templateName ?? null,
        odometer: next.odometer ?? '',
        comments: next.comments ?? '',
        completedAt: complete ? (next.completedAt || serverTimestamp()) : null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  async function discardInspection() {
    if (!jobId) return;
    await deleteDoc(doc(inspectionsCol(), jobId));
  }

  return { inspection, ready, startInspection, saveInspection, discardInspection };
}
